'use client'

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Badge } from '@/shared/components/ui/badge'
import {
  getAvailableVolunteersForDate,
  updateDailyVolunteers,
  JobBoardVolunteer,
  DayShift,
} from '../../api/work-orders.api'
import { Users, Search, Plus, Trash2, CheckCircle, AlertCircle, Loader2, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'

interface DailyVolunteerStaffingModalProps {
  isOpen: boolean
  onClose: () => void
  workOrder: any
  date: string
  regionId: number
  onSaved: () => void
}

export function DailyVolunteerStaffingModal({
  isOpen,
  onClose,
  workOrder,
  date,
  regionId,
  onSaved,
}: DailyVolunteerStaffingModalProps) {
  const [availableVolunteers, setAvailableVolunteers] = useState<JobBoardVolunteer[]>([])
  const [assignedVolunteerIds, setAssignedVolunteerIds] = useState<number[]>([])
  const [assignedVolunteersList, setAssignedVolunteersList] = useState<any[]>([])
  const [shift, setShift] = useState<DayShift>('FULL_DAY')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!isOpen || !workOrder || !date) return

    const loadData = async () => {
      setIsLoading(true)
      try {
        // Find existing assigned IDs and shift for this day from workOrder.workOrderHours
        let initialIds: number[] = []
        let initialShift: DayShift = 'FULL_DAY'
        if (Array.isArray(workOrder.workOrderHours)) {
          const entry = workOrder.workOrderHours.find((e: any) => e.date === date)
          if (entry) {
            if (Array.isArray(entry.volunteerIds)) {
              initialIds = entry.volunteerIds
            }
            if (entry.shift) {
              initialShift = entry.shift as DayShift
            }
          }
        }
        setShift(initialShift)
        // If empty, fallback to workOrder.volunteers
        if (initialIds.length === 0 && Array.isArray(workOrder.volunteers)) {
          initialIds = workOrder.volunteers.map((v: any) => v.id)
        }
        setAssignedVolunteerIds(initialIds)

        // Existing volunteers mapped
        const assignedObjs = (workOrder.volunteers || []).filter((v: any) =>
          initialIds.includes(v.id)
        )
        setAssignedVolunteersList(assignedObjs)

        // Fetch unbooked available volunteers for this date
        const res = await getAvailableVolunteersForDate(regionId, date, workOrder.id)
        if (res.success && res.data) {
          setAvailableVolunteers(res.data)
        }
      } catch (err) {
        console.error('Failed to load available volunteers:', err)
        toast.error('Failed to load available volunteers')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isOpen, workOrder, date, regionId])

  const handleAddVolunteer = (vol: JobBoardVolunteer) => {
    if (!assignedVolunteerIds.includes(vol.userId)) {
      setAssignedVolunteerIds([...assignedVolunteerIds, vol.userId])
      setAssignedVolunteersList([
        ...assignedVolunteersList,
        { id: vol.userId, fullname: vol.fullName, email: vol.email, role: vol.role },
      ])
      setAvailableVolunteers(availableVolunteers.filter((v) => v.userId !== vol.userId))
    }
  }

  const handleRemoveVolunteer = (userId: number) => {
    const nextIds = assignedVolunteerIds.filter((id) => id !== userId)
    setAssignedVolunteerIds(nextIds)
    const removed = assignedVolunteersList.find((v) => v.id === userId)
    setAssignedVolunteersList(assignedVolunteersList.filter((v) => v.id !== userId))
    if (removed) {
      setAvailableVolunteers([
        ...availableVolunteers,
        {
          userId: removed.id,
          volunteerId: removed.id,
          fullName: removed.fullname || removed.name || 'Volunteer',
          email: removed.email,
          experienceLeadingTeams: 0,
          experienceConstruction: 0,
          skills: {},
        },
      ])
    }
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const res = await updateDailyVolunteers(workOrder.id, date, assignedVolunteerIds, shift)
      if (res.success) {
        toast.success(`Staffing updated for ${date}`)
        onSaved()
        onClose()
      } else {
        toast.error(res.message || 'Failed to update daily staffing')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating daily staffing')
    } finally {
      setIsSaving(false)
    }
  }

  const volunteersNeeded = workOrder?.volunteersNeeded || 0
  const isFilled = volunteersNeeded > 0 && assignedVolunteerIds.length >= volunteersNeeded

  const filteredAvailable = availableVolunteers.filter((v) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return v.fullName.toLowerCase().includes(q) || (v.email && v.email.toLowerCase().includes(q))
  })

  // Format date display
  const dateFormatted = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="border-b pb-3">
          <DialogTitle className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              <span>Daily Volunteer Staffing</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {dateFormatted}
            </Badge>
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1">
            <strong>{workOrder?.workOrderType}</strong> for{' '}
            {workOrder?.home?.homeowner?.user?.fullname || workOrder?.teamName || 'Work Order'}
            {workOrder?.teamLead && (
              <span className="ml-2 font-medium text-foreground">
                (Team Lead: {workOrder.teamLead.fullname})
              </span>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-3 space-y-4">
          {/* Shift Selector */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-muted/20 p-3 rounded-lg border">
            <div>
              <div className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                <Clock className="w-3.5 h-3.5 text-primary" />
                <span>Work Day Shift</span>
              </div>
              <div className="text-[11px] text-muted-foreground">
                Sequential half-day jobs (Morning/Afternoon) share the crew without double-counting required worker slots.
              </div>
            </div>
            <div className="flex items-center gap-1 bg-background p-1 rounded-lg border shrink-0">
              <button
                type="button"
                onClick={() => setShift('FULL_DAY')}
                className={cn(
                  'px-2.5 py-1 rounded text-xs font-medium transition-all',
                  shift === 'FULL_DAY'
                    ? 'bg-primary text-primary-foreground shadow-xs font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Full Day (8h)
              </button>
              <button
                type="button"
                onClick={() => setShift('MORNING')}
                className={cn(
                  'px-2.5 py-1 rounded text-xs font-medium transition-all',
                  shift === 'MORNING'
                    ? 'bg-primary text-primary-foreground shadow-xs font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Morning (AM)
              </button>
              <button
                type="button"
                onClick={() => setShift('AFTERNOON')}
                className={cn(
                  'px-2.5 py-1 rounded text-xs font-medium transition-all',
                  shift === 'AFTERNOON'
                    ? 'bg-primary text-primary-foreground shadow-xs font-semibold'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                Afternoon (PM)
              </button>
            </div>
          </div>

          {/* Capacity Target Header */}
          <div className="flex items-center justify-between bg-muted/40 p-3 rounded-lg border">
            <div>
              <div className="text-xs font-semibold">Staffing Target</div>
              <div className="text-xs text-muted-foreground">
                Assigned: <span className="font-bold text-foreground">{assignedVolunteerIds.length}</span>
                {volunteersNeeded > 0 && <span> / {volunteersNeeded} Volunteers Needed</span>}
              </div>
            </div>
            {volunteersNeeded > 0 && (
              <Badge
                variant={isFilled ? 'default' : 'secondary'}
                className={isFilled ? 'bg-emerald-600' : ''}
              >
                {isFilled ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Target Met
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 text-amber-500" /> Need{' '}
                    {volunteersNeeded - assignedVolunteerIds.length} more
                  </span>
                )}
              </Badge>
            )}
          </div>

          {/* Assigned Volunteers on this date */}
          <div>
            <h4 className="text-xs font-semibold mb-2">Assigned Volunteers on {dateFormatted}</h4>
            {assignedVolunteersList.length === 0 ? (
              <div className="p-4 border rounded-lg text-center text-xs text-muted-foreground bg-muted/20">
                No volunteers assigned yet for this date. Pick from available volunteers below.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {assignedVolunteersList.map((vol) => (
                  <div
                    key={vol.id}
                    className="p-2 border rounded-md flex items-center justify-between gap-2 text-xs bg-background shadow-sm"
                  >
                    <div className="truncate">
                      <div className="font-medium truncate">{vol.fullname || vol.name}</div>
                      {vol.email && <div className="text-[10px] text-muted-foreground truncate">{vol.email}</div>}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveVolunteer(vol.id)}
                      className="h-6 w-6 text-destructive hover:bg-destructive/10 shrink-0"
                      title="Remove volunteer from this day"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Unbooked Volunteers Picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold">
                Available Volunteers for {dateFormatted} ({filteredAvailable.length})
              </h4>
              <span className="text-[10px] text-muted-foreground">
                (Excludes volunteers already booked on other work orders)
              </span>
            </div>

            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
                placeholder="Search available volunteers..."
                className="h-8 pl-8 text-xs"
              />
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-xs text-muted-foreground gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading available volunteers...
              </div>
            ) : filteredAvailable.length === 0 ? (
              <div className="p-4 border rounded-lg text-center text-xs text-muted-foreground">
                No unassigned volunteers available for this date.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                {filteredAvailable.map((vol) => (
                  <div
                    key={vol.userId}
                    className="p-2 border rounded-md flex items-center justify-between gap-2 text-xs bg-background hover:border-primary/40 transition-colors shadow-sm"
                  >
                    <div className="truncate">
                      <div className="font-medium truncate">{vol.fullName}</div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {vol.groupName || vol.role || 'Volunteer'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddVolunteer(vol)}
                      className="h-6 text-[11px] px-2 text-primary hover:bg-primary/10 gap-0.5 shrink-0"
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="border-t pt-3 flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="bg-primary hover:bg-primary/90 text-white font-bold"
          >
            {isSaving ? 'Saving...' : 'Save Staffing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
